/* SPEC-462 — the SuryaIQ mark: concept B, "Planning Dial" (Param's pick, 03 Oct).
 *
 * A solid sun disc inside one open orbit ring; the gap in the ring holds a single node,
 * which marks "now" on the planning cycle. Ported from
 * `.agents/reference/design/logo-concepts.html` (symbol `mark-b`) on its 24-unit grid —
 * the path and circle coordinates below are that file's, unchanged.
 *
 * Theming: the ring is `currentColor`, and the disc + node are `--mark-accent` (defined in
 * theme.css) falling back to `currentColor`. So the mark renders in ONE colour wherever the
 * accent token is unset, and a surface with its own palette (the Stats shell) re-points the
 * accent locally instead of forking the drawing.
 *
 * Two drawings, one component:
 *   `full`  — the concept mark, for 20px and up (rail, sidebar lockup, login).
 *   `small` — the 16px variant the design notes require. At 16px the full mark's node and
 *             gap close up and it reads as a "target"/"record" glyph, so this one has a
 *             thicker ring (2.5u), a wider gap (60° vs 45°) and the node MERGED into the
 *             ring's end instead of floating in the gap. favicon.svg is this drawing.
 * `variant="auto"` (the default) picks `small` at size ≤ 16.
 *
 * Accessibility: decorative by default (`aria-hidden`), because every site that shows the
 * mark also shows the "SuryaIQ" wordmark or an accessible name beside it. Pass `title` to
 * make it a labelled image where it stands alone. */
import React from 'react';

interface SuryaMarkProps {
  /** Rendered width and height in px. */
  size?: number;
  variant?: 'full' | 'small' | 'auto';
  /** Accessible name. Omit when a visible wordmark or label already names the brand. */
  title?: string;
  className?: string;
  style?: React.CSSProperties;
}

const ACCENT: React.CSSProperties = { fill: 'var(--mark-accent, currentColor)' };

export function SuryaMark({ size = 24, variant = 'auto', title, className, style }: SuryaMarkProps) {
  const small = variant === 'small' || (variant === 'auto' && size <= 16);
  const a11y = title
    ? { role: 'img' as const, 'aria-label': title }
    : { 'aria-hidden': true as const, focusable: 'false' as const };
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      style={style}
      {...a11y}
    >
      {small ? (
        <>
          {/* Ring from -85° round to -25° (a 300° arc, 60° gap at upper right). */}
          <path
            d="M12.78 3.03 A9 9 0 1 0 20.16 8.2"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            strokeLinecap="round"
          />
          {/* The node, merged into the ring's end. */}
          <circle cx="20.16" cy="8.2" r="2.4" style={ACCENT} />
          <circle cx="12" cy="12" r="4.9" style={ACCENT} />
        </>
      ) : (
        <>
          <path
            d="M14.33 3.31 A9 9 0 1 0 19.79 7.5"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.2}
            strokeLinecap="round"
          />
          <circle cx="17.48" cy="4.86" r="1.7" style={ACCENT} />
          <circle cx="12" cy="12" r="4.6" style={ACCENT} />
        </>
      )}
    </svg>
  );
}
